import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  View,
  Text,
  Platform,
  TouchableOpacity,
  TextInput,
  Alert,
  StyleSheet,
  ActivityIndicator,
} from "react-native";
import ActionSheetIOS from "../../utils/helpers/ActionSheetIOS";
import { AntDesign } from "@expo/vector-icons";
import { KeyboardAwareScrollView } from "react-native-keyboard-aware-scroll-view";
import { useSelector } from "react-redux";
import * as ImagePicker from "expo-image-picker";
import { Picker } from "@react-native-picker/picker";
import {
  getFirestore,
  doc,
  collection,
  getDocs,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { firebaseApp } from "../../firebase/config";
import UploadPhotoBox from "../../components/UploadPhotoBox";
import { uploadImage } from "../../utils/helpers/functions";
import Swal from "sweetalert2";
import SectionCard from "../../components/SectionCard";
import SelectField from "../../components/SelectField";
import TextAreaField from "../../components/TextAreaField";
import EmptyState from "../../components/EmptyState";
import {
  executeFirestoreWriteWithQueue,
  isDeviceOnline,
} from "../../utils/offlineWriteQueue";
import { queueDeferredMediaUpload } from "../../utils/deferredMediaQueue";

const DefectReportingFormScreen = ({ route, navigation }) => {
  const [selectedEquipment, setSelectedEquipment] = useState("");
  const [summary, setSummary] = useState("");
  const [severity, setSeverity] = useState("medium");
  const [showValidation, setShowValidation] = useState(false);
  const [equipmentNewServiceStatus, setEquipmentNewServiceStatus] =
    useState(false);
  const [allEquipment, setAllEquipment] = useState([]);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const currentClean = useSelector((state) => state.buildingList.currentClean);
  const currentDrop = useSelector((state) => state.buildingList.currentDrop);

  const userUid = useSelector((state) => state.auth.uid);

  const { siteId } = route.params;
  const date = new Date();
  const db = getFirestore(firebaseApp);
  const siteRef = doc(db, "BuildingsX", siteId);

  const equipmentRef = collection(siteRef, "equipment");
  const defectsRef = collection(siteRef, "defects");

  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    getDefectOptions();
    return () => {
      isMounted.current = false;
    };
  }, []);

  const getDefectOptions = async () => {
    try {
      const allEquipment = await getDocs(equipmentRef);
      const equipmentArray = [];
      const buildingDefectOption = {
        id: "building",
        name: "Building",
        type: "building",
      };
      if (!allEquipment.empty) {
        allEquipment.forEach((eq) => {
          equipmentArray.push({ id: eq.id, ...eq.data() });
        });
      }
      if (isMounted.current) {
        setAllEquipment([...equipmentArray, buildingDefectOption]);
        setLoading(false);
      }
    } catch (error) {
      console.log(error);
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const isEquipment = !(
    selectedEquipment === "" || selectedEquipment === "building"
  );

  const severityOptions = useMemo(
    () => [
      { label: "High (urgent)", value: "high" },
      { label: "Medium", value: "medium" },
      { label: "Low", value: "low" },
    ],
    []
  );

  const selectedEquipmentData =
    !!selectedEquipment &&
    allEquipment.find((eq) => eq.id === selectedEquipment);

  const isInService = selectedEquipmentData?.inService;

  const labelForEquipmentServiceBox = isInService
    ? "Not in service?"
    : "In service?";

  const equipmentStatus =
    isInService && equipmentNewServiceStatus
      ? true
      : isInService && !equipmentNewServiceStatus
      ? false
      : !isInService && equipmentNewServiceStatus
      ? true
      : false;

  const openFormMenuOnIos = () => {
    if (!ActionSheetIOS) {
      return Alert.alert(
        "Unsupported",
        "Action sheets are only supported on iOS in this web build."
      );
    }

    ActionSheetIOS.showActionSheetWithOptions(
      {
        options: ["Cancel", ...allEquipment.map((e) => e.name)],
        cancelButtonIndex: 0,
        userInterfaceStyle: "dark",
      },
      (buttonIndex) => {
        if (buttonIndex === 0) {
          return setSelectedEquipment("");
        }
        setSelectedEquipment(allEquipment[buttonIndex - 1].id);
      }
    );
  };

  const pickMedia = async () => {
    if (media.length >= 5) {
      return Swal.fire({
        title: "Oops",
        text: "You can only upload a maximum of 5 files.",
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#5e99fa",
      });
    }
    const selectedFiles = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.All,
      allowsMultipleSelection: true,
      allowsEditing: true,
      aspect: [4, 3],
      quality: 1,
      multiple: true,
      videoMaxDuration: 20,
    });
    // Support both legacy and modern response shapes from expo-image-picker
    const assets = selectedFiles?.assets || (selectedFiles?.uri ? [{ uri: selectedFiles.uri }] : []);
    const filteredFiles = (assets || []).filter(
      (file) => file?.uri && !file.uri.endsWith(".svg") && !file.uri.endsWith(".mp3")
    );

    if (filteredFiles.length) {
      const uris = filteredFiles.map((f) => f.uri).filter(Boolean);
      setMedia((prev) => [...prev, ...uris].slice(0, 5));
    }
  };

  const summaryTrimmed = summary.trim();
  const isHighSeverity = severity === "high";
  const severityError = showValidation && !severity ? "Please choose a severity." : "";
  const equipmentError =
    showValidation && !selectedEquipment ? "Please select equipment or building." : "";
  const summaryError =
    showValidation && !summaryTrimmed ? "Please add a summary for this defect." : "";
  const mediaError =
    showValidation && media.length === 0 ? "Add at least one photo or video." : "";
  const priorityRationaleError =
    showValidation && isHighSeverity
      ? "Please explain why this is high severity."
      : "";

  const isValid =
    Boolean(selectedEquipment) &&
    Boolean(summaryTrimmed) &&
    Boolean(severity) &&
    media.length > 0

  const submitFormHandler = async () => {
    setShowValidation(true);
    if (!isValid) {
      const msg = "Please select equipment, choose severity, add summary, and include at least one image.";
      if (Platform.OS === "web") return Swal.fire({ icon: 'error', title: 'Missing fields', text: msg });
      return Alert.alert('Missing fields', msg);
    }

    if (!selectedEquipmentData?.id || !selectedEquipmentData?.type) {
      const msg = "Selected equipment details are unavailable. Please re-select equipment and try again.";
      if (Platform.OS === "web") return Swal.fire({ icon: "error", title: "Invalid selection", text: msg });
      return Alert.alert("Invalid selection", msg);
    }

    if(isEquipment) {
      const userResponse = await Swal.fire({
        title: "Are you sure?",
        text: `You are about to put the equipment ${equipmentStatus ? "back to" : "out of"} service. Are you sure?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        confirmButtonColor: "#5e99fa",
        cancelButtonColor: "#d33",
      });
  
      if (!userResponse.isConfirmed) return;

    }

    setSubmitting(true);
    try {
      const isOnline = await isDeviceOnline();
      const shouldQueueMediaUpload = !isOnline && media.length > 0;
      const uploadedMediaUrl = shouldQueueMediaUpload
        ? []
        : await uploadImage(media, selectedEquipmentData.id);

      const normalizedDropId = currentDrop?.name || "";
      const defectStatus = "open";

      const documentToAdd = {
        defectReportDate: date,
        fixDate: null,
        defectType: selectedEquipmentData.type,
        defectTypeLabel: selectedEquipmentData.name || selectedEquipmentData.type,
        inService: equipmentStatus,
        summary: summaryTrimmed,
        priority: severity,
        status: defectStatus,
        isResolved: false,
        completed: false,
        images: uploadedMediaUrl,
        completedBy: userUid,
        equipmentId: selectedEquipmentData.id,
        cleanId: currentClean,
        dropId: normalizedDropId,
        source: 'DefectReportingForm',
      };

      const docRef = doc(defectsRef);
      const queuedMediaUploadId = shouldQueueMediaUpload
        ? await queueDeferredMediaUpload({
            label: "defect-report",
            mediaUris: media,
            storageBasePath: `defects/${siteId}/${docRef.id}`,
            documentPaths: [docRef.path],
          })
        : null;

      const { queued } = await executeFirestoreWriteWithQueue({
        db,
        queueLabel: "defect-report",
        writeOperation: async () => {
          await setDoc(docRef, {
            ...documentToAdd,
            images: shouldQueueMediaUpload ? [] : uploadedMediaUrl,
            mediaUploadStatus: shouldQueueMediaUpload ? "queued" : "uploaded",
            pendingMediaCount: shouldQueueMediaUpload ? media.length : 0,
            queuedMediaUploadId: queuedMediaUploadId || null,
          });

          if (selectedEquipmentData.type !== "building") {
            await updateDoc(doc(equipmentRef, selectedEquipmentData.id), {
              inService: equipmentStatus,
              lastReportId: docRef.id,
            });
          }
        },
      });

      // Reset only when component is still mounted
      if (isMounted.current) {
        setEquipmentNewServiceStatus(false);
        setSelectedEquipment("");
        setSummary("");
        setSeverity("medium");
        setShowValidation(false);
        setMedia([]);
      }

      const alertMessage = queued
        ? shouldQueueMediaUpload
          ? "Offline: defect report saved. Photos queued for upload."
          : "Offline: defect report saved locally and queued for sync."
        : "Thanks, report has been successfully submitted.";
      if (Platform.OS === "web") {
        await Swal.fire({ title: "Thanks", text: alertMessage, icon: "success", confirmButtonText: "OK", confirmButtonColor: "#5e99fa" });
        navigation.navigate("Building");
      } else {
        Alert.alert("Thanks", alertMessage, [{ text: "OK", onPress: () => navigation.navigate("Building") }]);
      }
    } catch (error) {
      console.log(error);
      const message = error?.message || 'Unable to submit the report. Please try again.';
      if (Platform.OS === 'web') Swal.fire({ icon: 'error', title: 'Error', text: message });
      else Alert.alert('Oops', message);
    } finally {
      if (isMounted.current) setSubmitting(false);
    }
  };

  if (loading)
    return (
      <View style={styles.loadingWrapper}>
        <ActivityIndicator size="large" color="#2563eb" />
        <View style={styles.skeletonCard} />
        <View style={styles.skeletonCard} />
      </View>
    );
  return (
    <KeyboardAwareScrollView
      contentContainerStyle={{ flex: 1 }}
      enableOnAndroid={true}
      automaticallyAdjustContentInsets={true}
      enableAutomaticScroll={Platform.OS === "ios"}
    >
      <View style={styles.root}>
        <Text style={styles.pageTitle}>Report a defect</Text>
        <Text style={styles.pageSubtitle}>Select the equipment or building, add a summary and attach evidence.</Text>

        <SectionCard title="Defect" subtitle="Choose equipment or report a building defect.">
          {Platform.OS === 'ios' ? (
            <TouchableOpacity onPress={openFormMenuOnIos} style={styles.iosButton}>
              <Text style={styles.iosButtonText}>{selectedEquipmentData?.name || 'Please select'}</Text>
            </TouchableOpacity>
          ) : (
            <SelectField
              label="Equipment"
              value={selectedEquipment}
              options={allEquipment.map((s) => ({ label: s.name, value: s.id }))}
              onValueChange={setSelectedEquipment}
              placeholder="Select equipment or building"
              helperText="Choose the equipment this defect relates to, or select 'Building'."
              error={equipmentError}
            />
          )}

          {isEquipment && selectedEquipmentData && (
            <View style={styles.inlineRow}>
              <Text style={styles.smallText}>Status:</Text>
              <Text style={[styles.statusText, { color: isInService ? 'green' : 'red' }]}>{isInService ? 'In service' : 'Out of service'}</Text>
              <TouchableOpacity style={styles.toggle} onPress={() => setEquipmentNewServiceStatus((s) => !s)}>
                {equipmentNewServiceStatus && <AntDesign name="check" size={16} color="#111" />}
              </TouchableOpacity>
              <Text style={styles.smallText}>{labelForEquipmentServiceBox}</Text>
            </View>
          )}
        </SectionCard>

        <SectionCard title="Details" subtitle="Describe the defect and attach photos.">
          <SelectField
            label="Severity"
            value={severity}
            options={severityOptions}
            onValueChange={setSeverity}
            placeholder="Select severity"
            helperText="This drives PDF highlighting and escalation."
            error={severityError}
          />

          <TextAreaField
            label="Summary"
            value={summary}
            onChangeText={setSummary}
            placeholder="Example: Level 4 east facade, near access hatch"
            helperText="Helpful for locating the defect faster."
            error={summaryError}
          />

          <UploadPhotoBox pickImage={pickMedia} media={media} />
          {!!mediaError && <Text style={styles.validationText}>{mediaError}</Text>}

          {media.length > 0 && (
            <TouchableOpacity onPress={() => setMedia([])} style={styles.clearMediaBtn}>
              <Text style={styles.clearMediaText}>Clear media</Text>
            </TouchableOpacity>
          )}
        </SectionCard>

        <TouchableOpacity
          style={[styles.submitButton, { backgroundColor: isValid ? '#2563eb' : '#9ca3af' }]}
          disabled={!isValid || submitting}
          onPress={submitFormHandler}
          activeOpacity={0.85}
        >
          <Text style={styles.submitButtonText}>{submitting ? 'Submitting...' : 'Submit report'}</Text>
          {submitting && <ActivityIndicator color="#fff" style={{ marginLeft: 8 }} />}
        </TouchableOpacity>
      </View>
    </KeyboardAwareScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: "center",
    backgroundColor: "#fff",
    textAlign: "center",
    paddingVertical: 10,
    paddingHorizontal: 30,
  },
  title: {
    fontSize: 30,
  },
  date: {
    fontSize: 20,
    fontStyle: "italic",
  },
  section: {
    marginVertical: 10,
    marginHorizontal: 30,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  picker: {
    width: 200,
    height: 50,
    borderRadius: 10,
    marginTop: 5,
    color: "black",
    backgroundColor: "#F6F6F6",
  },
  iosButton: {
    width: 200,
    height: 50,
    borderRadius: 10,
    marginTop: 5,
    backgroundColor: "#F6F6F6",
    justifyContent: "center",
  },
  iosButtonText: {
    color: "black",
    paddingHorizontal: 10,
  },
  submitButton: {
    height: 59,
    borderRadius: 10,
    justifyContent: "center",
    width: 200,
    marginTop: 20,
    alignSelf: "center",
  },
  submitButtonText: {
    color: "#fff",
    alignSelf: "center",
  },
  summaryInput: {
    width: 300,
    height: 100,
    borderRadius: 10,
    backgroundColor: "white",
    marginTop: 10,
    borderColor: "#E8EAE6",
    borderWidth: 1,
    padding: 10,
    alignSelf: "center",
  },
  radioButton: {
    height: 20,
    width: 20,
    borderRadius: 20 / 2,
    borderWidth: 1,
    borderColor: "#000",
    justifyContent: "flex-start",
  },
  outOfServiceBox: {
    width: 290,
    flexDirection: "row",
    justifyContent: "space-between",
    marginVertical: 10,
  },
  root: {
    flex: 1,
    backgroundColor: "#f1f5f9",
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 28,
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: "800",
    color: "#0f172a",
    marginBottom: 6,
  },
  pageSubtitle: {
    fontSize: 14,
    color: "#475569",
    marginBottom: 14,
    lineHeight: 20,
  },
  inlineRow: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginTop: 2,
    marginBottom: 12,
  },
  smallText: {
    fontSize: 13,
    color: "#334155",
  },
  statusText: {
    fontWeight: "700",
    fontSize: 13,
  },
  toggle: {
    width: 20,
    height: 20,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#64748b",
    alignItems: "center",
    justifyContent: "center",
    marginHorizontal: 4,
    backgroundColor: "#ffffff",
  },
  clearMediaBtn: {
    alignSelf: "flex-start",
    marginTop: 8,
  },
  clearMediaText: {
    color: "#1d4ed8",
    fontSize: 13,
    fontWeight: "600",
  },
  loadingWrapper: {
    flex: 1,
    backgroundColor: "#f1f5f9",
    paddingHorizontal: 16,
    paddingTop: 20,
  },
  skeletonCard: {
    height: 180,
    backgroundColor: "#e2e8f0",
    borderRadius: 16,
    marginTop: 16,
  },
  validationText: {
    marginTop: 8,
    fontSize: 12,
    color: "#b91c1c",
  },
});

export default DefectReportingFormScreen;
